import { ChangeDetectorRef, Component, NgModule, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AuthService, DocumentService, WorkspaceService } from '@app/core/services';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { SystemLogoModule } from '../../atoms/logo/logo.component';
import { SystemIconComponentModule } from '../../atoms/icon/icon.component';
import { SystemButtonModule } from '../../atoms/button/button.directive';
import { SystemIconDirectiveModule } from '../../atoms/icon-directive/icon.directive';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, finalize, Subject, takeUntil, tap } from 'rxjs';
import { NewModalComponent, SystemNewModalModule } from '../newModal/new-modal.component';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { MENU_ADMIN, MENU_GENERAL, ROLE } from './sidebar.model';
import { CreateOrderModalModule } from '../../../../pages/dashboard/components/create-order/create-order.module';
import { CreateOrderComponent } from '@app/pages/dashboard/components/create-order/create-order.component';
import { OrderService } from '@app/core/services/orders/order.service';
import { CreateOrderService } from '@app/core/services/create-order/create-order.service';
import { SystemInputSetModule } from '../input-set/input-set.module';
import { InputType } from '../input-set/input-set.model';
import { MessageTypes, SystemToastModule } from '../../atoms';
import { CalendlyModule } from '@app/shared/calendly/calendly.module';
declare global {
  interface Window {
    Calendly: any;
  }
}

@Component({
  selector: 'app-system-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent implements OnInit, OnDestroy {
  @ViewChild(NewModalComponent) newModalComponent!: NewModalComponent;
  @ViewChild(CreateOrderComponent) createOrderComponent!: CreateOrderComponent;
  menu: any;
  dataUser: any;
  nameDocument = '';
  userId!: string;
  loading = false;
  sendingData = false;
  showWidgetCalendar = false;

  /** Order Creation */
  inputType = InputType;
  createOrderForm!: FormGroup;
  /** Toast message */
  toastMessage = '';
  /** Type toast message */
  typeToastMessage = '';

  /** show toast message */
  showToastMessage = false;

  /** Messages types */
  messageTypes = MessageTypes;
  /** Files selected list */
  filesSelectedList: File[] = [];
  /** Duration toast message */
  durationToastMessage = 5000;

  private destroy$ = new Subject<void>();

  constructor(
    private _authService: AuthService,
    private _documentService: DocumentService,
    private _workspaceService: WorkspaceService,
    private _orderService: OrderService,
    private _createOrderService: CreateOrderService,
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    private _fb: FormBuilder,
    private _translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeOrderForm();
    this._createOrderService.state$.pipe(takeUntil(this.destroy$)).subscribe((state: boolean) => {
      if (!state) {
        this.closeModalOrder();
      } else {
        this.showWidgetCalendar = false;
        this.openModalOrder();
      }
      this._cdr.detectChanges();
    });

    this._createOrderService.skipCreateOrder$.pipe(takeUntil(this.destroy$)).subscribe((state: boolean) => {
      if (state) {
        this.showWidgetCalendar = true;
        this.openModalOrder();
      } else {
        this.showWidgetCalendar = false;
      }
      this._cdr.detectChanges();
    });

    this.dataUser = JSON.parse(`${localStorage.getItem('dataUser')}`);
    const menu =
      this.dataUser.rol == ROLE.ADMIN || this.dataUser.rol == ROLE.ADMIN_COMPANY || this.dataUser.rol == ROLE.GOD
        ? MENU_ADMIN
        : MENU_GENERAL;

    this.menu = menu;
    this.setSidebarMenu(this.menu);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async createDocument() {
    this._authService.isLoggedIn.pipe(takeUntil(this.destroy$)).subscribe((isLoggedIn: any) => {
      if (!isLoggedIn) {
        this.userId = this.dataUser.user_id;
      }
    });

    const body = {
      name: this.nameDocument,
      owner: this.userId,
      description: 'file without description',
      filename: this.nameDocument,
      variables: [],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      html_content: {
        body: '',
        header: '',
        footer: '',
        style: '',
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      status_doc: 'DRAFT',
      expire: '2025-03-03',
    };
    try {
      this._documentService
        .createEmptyDocument(body)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: any) => {
          if (!data.error) {
            this._workspaceService.setURL(data.data._id);
          } else {
            this.typeToastMessage = this.messageTypes.ERROR;
            this.toastMessage = this.translateText('ERROR CREATING DOCUMENT');
            this.showToastMessage = true;
          }
        });
    } catch (error) {
      console.error(error);
      this.typeToastMessage = this.messageTypes.ERROR;
      this.toastMessage = this.translateText('ERROR CREATING DOCUMENT');
      this.showToastMessage = true;
    }
  }
  /** Change document order
   * @param e event with the files selected
   */
  changeDocumentOrder(e: any) {
    if (e && e.files) {
      const files: File[] = Array.from(e.files);
      this.filesSelectedList.push(...files);
      this.createOrderForm.controls.files.setValue(this.filesSelectedList);
    }
  }
  /** Remove file from list
   * @param index index of the file to remove
   */
  removeFileFromList(index: number) {
    this.filesSelectedList.splice(index, 1);
    this.createOrderForm.controls.files.setValue(this.filesSelectedList);
  }

  initializeOrderModal(): void {
    this._createOrderService.setState(true);
  }

  openModal() {
    this.newModalComponent.open();
  }

  closeModal() {
    this.newModalComponent.close();
  }

  openModalOrder() {
    this.createOrderComponent.open();
  }

  closeModalOrder() {
    if (!this.createOrderComponent) return;
    this.createOrderComponent.close();
    this.showWidgetCalendar = false;
    this.filesSelectedList = [];
    this.createOrderForm.reset();
  }

  createOrder() {
    this.loading = true;
    if (this.createOrderForm.valid) {
      this.sendingData = true;
      const request = this.mapFormToRequest();

      this._orderService
        .createOrder(request)
        .pipe(
          takeUntil(this.destroy$),
          tap(data => {
            if (data.error) {
              this.durationToastMessage = 5000;
              this.typeToastMessage = this.messageTypes.ERROR;
              this.toastMessage = this.translateText('UNEXPECTED ERROR');
            } else {
              this._createOrderService.setOrderState(data.data); //*Send order to update the observable
              this._router.navigate(['/panel/orders']);
              this.typeToastMessage = this.messageTypes.SUCCESS;
              const responseData = data.data as { _id: string };
              this.toastMessage = this.translateText('ORDER CREATED', { orderNumber: responseData._id.slice(-6) });
              this.durationToastMessage = 10000;
              this.showWidgetCalendar = true;
              this.filesSelectedList = [];
              this.createOrderForm.reset();
            }
          }),
          finalize(() => {
            this.loading = false;
            this.sendingData = false;
            this.showToastMessage = true;
            this._cdr.detectChanges();
          }),
          catchError(error => {
            this.durationToastMessage = 5000;
            this.typeToastMessage = this.messageTypes.ERROR;
            this.toastMessage = this.translateText('UNEXPECTED ERROR');

            return error;
          })
        )
        .subscribe();
    }
  }
  private setSidebarMenu(menu: any) {
    const usersAllowedForFormEntries = [
      '6388e4fd70ecce183783b13c',
      '628e39a3ef88905953b1b8b7',
      '65fc452983ac5d063aa52b0f',
      '65d79e5783ac5d063aa50585', // Yeny Hinestroza - PROD
    ];

    if (usersAllowedForFormEntries.some(id => this.dataUser.user_id === id)) {
      const formEntriesExists = menu.find((item: any) => item.text === 'FORM ENTRIES');
      if (!formEntriesExists) {
        menu.splice(2, 0, {
          route: '/panel/form-entries',
          text: 'FORM ENTRIES',
          icon: 'folder-duplicate',
          tooltip: 'Form entries',
        });
      }
    }
  }

  private mapFormToRequest(): any {
    const files = this.filesSelectedList;
    const formData = new FormData();

    files.forEach((file: any) => {
      formData.append('files', file);
    });
    formData.append('name', this.createOrderForm.get('orderName')?.value);
    formData.append('description', this.createOrderForm.get('description')?.value);
    formData.append('applicant', this.dataUser.user_id);
    formData.append('status', 'PENDING');
    formData.append('type', 'DOCUMENT');
    if (this.dataUser.company_id) {
      formData.append('company', this.dataUser.company_id);
    }

    return formData;
  }

  private initializeOrderForm() {
    this.createOrderForm = this._fb.group({
      orderName: ['', [Validators.required]],
      files: ['', [Validators.required]],
      description: ['', [Validators.required]],
    });
    this.filesSelectedList = [];
  }

  private translateText(str: string, params?: any): string {
    return this._translate.instant('DASHBOARD.ORDER.' + str.toUpperCase(), params);
  }
}

@NgModule({
  declarations: [SidebarComponent],
  exports: [SidebarComponent],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SystemLogoModule,
    SystemIconComponentModule,
    SystemButtonModule,
    SystemIconDirectiveModule,
    TranslateModule,
    SystemNewModalModule,
    CreateOrderModalModule,
    SystemInputSetModule,
    SystemToastModule,
    CalendlyModule,
  ],
})
export class SystemSidebarModule {}
